import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DownloadIcon from "@mui/icons-material/Download";
import DOMPurify from "dompurify";
import type { Address, Email } from "postal-mime";
import SupervisorApi from "../apis/supervisorApi";
import type { TicketAttachment } from "../types/supervisorType";
import { triggerBlobDownload } from "../utils/downloadUtils";

const api = new SupervisorApi();
const TEXT_PREVIEW_BYTES = 512 * 1024;

function previewKind(fileName: string, contentType: string): string {
  const type = contentType.split(";")[0].trim().toLowerCase();
  if (/^image\/(png|jpeg|gif|webp|avif|bmp)$/.test(type)) return "image";
  if (type === "application/pdf") return "pdf";
  if (type === "message/rfc822" || /\.eml$/i.test(fileName)) return "email";
  if (type === "text/html" || type === "application/xhtml+xml") return "html";
  if (type.startsWith("text/") || /\.(txt|md|csv|tsv|json|xml|yaml|yml|log|html?|svg)$/i.test(fileName)) return "text";
  return "file";
}

function addressText(address?: Address): string {
  if (!address) return "";
  if (address.group) return address.group.map(addressText).join(", ");
  return address.name ? `${address.name} <${address.address}>` : address.address || "";
}

function previewHtml(html: string): string {
  const clean = DOMPurify.sanitize(html, {
    FORBID_TAGS: ["form", "iframe", "object", "embed", "link", "meta", "svg", "math"],
    FORBID_ATTR: ["srcset"],
    ALLOWED_URI_REGEXP: /^(?:data:image\/(?:png|jpeg|gif|webp);base64,|#)/i,
  });
  return `<!doctype html><html><head><meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data:; style-src 'unsafe-inline'; base-uri 'none'; form-action 'none'"><style>body{font:14px/1.5 Arial,sans-serif;margin:0;overflow-wrap:anywhere}img,table{max-width:100%}</style></head><body>${clean}</body></html>`;
}

function HtmlPreview({ html, title }: { html: string; title: string }) {
  return <iframe title={title} sandbox="allow-same-origin" srcDoc={previewHtml(html)} style={{ width: "100%", height: 160, border: 0 }}
    onLoad={event => { const frame = event.currentTarget; frame.style.height = `${Math.min(420, Math.max(100, (frame.contentDocument?.body.scrollHeight || 100) + 20))}px`; }} />;
}

function AttachmentBody({ blob, url, fileName }: { blob: Blob; url: string; fileName: string }) {
  const kind = previewKind(fileName, blob.type);
  const [text, setText] = useState<string | null>(null);
  const [email, setEmail] = useState<Email | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    if (kind === "text" || kind === "html") {
      blob.slice(0, TEXT_PREVIEW_BYTES).text().then(value => { if (active) setText(value); })
        .catch(() => { if (active) setError("Unable to preview this text file. You can still download it."); });
    } else if (kind === "email") {
      import("postal-mime").then(({ default: PostalMime }) => PostalMime.parse(blob))
        .then(value => { if (active) setEmail(value); })
        .catch(() => { if (active) setError("Unable to preview this email. You can still download it."); });
    }
    return () => { active = false; };
  }, [blob, kind]);

  if (error) return <Alert severity="warning">{error}</Alert>;
  if (kind === "image") return <Box component="img" src={url} alt={fileName} sx={{ display: "block", maxWidth: "100%", maxHeight: 420, objectFit: "contain" }} />;
  if (kind === "pdf") return <iframe src={url} title={fileName} style={{ width: "100%", height: 420, border: 0 }} />;
  if (kind === "html") return text === null ? <CircularProgress size={24} /> : <HtmlPreview html={text} title={fileName} />;
  if (kind === "text") return text === null ? <CircularProgress size={24} /> : <>
    <Box component="pre" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", maxHeight: 420, overflow: "auto", m: 0 }}>{text}</Box>
    {blob.size > TEXT_PREVIEW_BYTES && <Typography variant="caption">Preview truncated. Download the file to view all content.</Typography>}
  </>;
  if (kind === "email") {
    if (!email) return <CircularProgress size={24} />;
    return <Box sx={{ display: "grid", gap: 1.5 }}>
      <Typography variant="h6">{email.subject || "(No subject)"}</Typography>
      <Typography variant="body2">From: {addressText(email.from)}</Typography>
      <Typography variant="body2">To: {(email.to || []).map(addressText).join(", ")}</Typography>
      {email.date && <Typography variant="body2">Date: {email.date}</Typography>}
      {email.html ? <HtmlPreview title="Email body" html={email.html} />
        : email.text ? <Box component="pre" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", maxHeight: 420, overflow: "auto", m: 0 }}>{email.text}</Box>
          : <Typography>This email has no readable message body.</Typography>}
      {email.attachments.map((attachment, index) => <Button key={index} startIcon={<DownloadIcon />} sx={{ justifySelf: "start", textTransform: "none" }}
        onClick={() => triggerBlobDownload(new Blob([attachment.content], { type: attachment.mimeType }), attachment.filename || `attachment-${index + 1}`)}>
        {attachment.filename || `Attachment ${index + 1}`}
      </Button>)}
    </Box>;
  }
  return <Typography>Download this file to open it in its application.</Typography>;
}

export default function TicketAttachmentView({ ticketId, attachment }: { ticketId: string; attachment: TicketAttachment }) {
  const container = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const fileName = attachment.fileName || `attachment-${attachment.attachmentId}`;

  useEffect(() => {
    if (attachment.attachmentType !== "FILE_ATTACHMENT" || !container.current) return;
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        setVisible(true);
        observer.disconnect();
      }
    }, { rootMargin: "120px" });
    observer.observe(container.current);
    return () => observer.disconnect();
  }, [attachment.attachmentType]);

  useEffect(() => {
    if (!visible || blob) return;
    let active = true;
    setError("");
    api.downloadTicketAttachment(ticketId, attachment.attachmentId).then(value => {
      if (!active) return;
      if ((!value.type || value.type === "application/octet-stream") && /\.pdf$/i.test(fileName)) {
        setBlob(new Blob([value], { type: "application/pdf" }));
      } else setBlob(value);
    }).catch(err => { if (active) setError(err.message || "Unable to load this attachment."); });
    return () => { active = false; };
  }, [visible, blob, ticketId, attachment.attachmentId, fileName, retry]);

  useEffect(() => {
    if (!blob) return;
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [blob]);

  if (attachment.attachmentType !== "FILE_ATTACHMENT") return <Typography>{fileName}</Typography>;

  return <Box ref={container} sx={{ display: "grid", gap: 1, my: 1 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
      <AttachFileIcon fontSize="small" />
      <Typography variant="body2" sx={{ overflowWrap: "anywhere", flex: 1 }}>{fileName}</Typography>
      {blob && url && <Button component="a" href={url} download={fileName} startIcon={<DownloadIcon />} size="small">Download</Button>}
    </Box>
    {error ? <Alert severity="error" action={<Button color="inherit" onClick={() => setRetry(value => value + 1)}>Retry</Button>}>{error}</Alert>
      : blob && url ? <AttachmentBody blob={blob} url={url} fileName={fileName} /> : visible && <CircularProgress size={24} />}
  </Box>;
}
